import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
/** Quick-nav destinations. Client-side only — jumps between existing pages. */
const DESTINATIONS = [
    { label: 'Home', group: 'Platform', to: '/' },
    { label: 'My products', group: 'Platform', to: '/my-products' },
    { label: 'Billing', group: 'Platform', to: '/billing', keywords: 'subscription invoice plan' },
    { label: 'Help & docs', group: 'Platform', to: '/help' },
    { label: 'SSL · Dashboard', group: 'SSL Monitor', to: '/products/ssl/dashboard' },
    { label: 'SSL · Domains', group: 'SSL Monitor', to: '/products/ssl/domains', keywords: 'certificate cert host' },
    { label: 'SSL · Domain groups', group: 'SSL Monitor', to: '/products/ssl/groups' },
    { label: 'SSL · Alert rules', group: 'SSL Monitor', to: '/products/ssl/alerting/rules', keywords: 'expiry threshold' },
    { label: 'SSL · Alert templates', group: 'SSL Monitor', to: '/products/ssl/alerting/templates' },
    { label: 'SSL · Delivery log', group: 'SSL Monitor', to: '/products/ssl/alerting/log' },
    { label: 'SSL · Reports', group: 'SSL Monitor', to: '/products/ssl/reports', keywords: 'history exports' },
    { label: 'Uptime · Monitors', group: 'Uptime Monitor', to: '/products/uptime/monitors', keywords: 'ping http check' },
    { label: 'URL Shortener · Links', group: 'URL Shortener', to: '/products/url-shortener/links', keywords: 'short link' },
    { label: 'Alerts · Groups', group: 'Alerting', to: '/alerts/groups', keywords: 'recipients' },
    { label: 'Alerts · Templates', group: 'Alerting', to: '/alerts/templates' },
    { label: 'Alerts · Delivery log', group: 'Alerting', to: '/alerts/delivery-log' },
    { label: 'Alerts · Providers', group: 'Alerting', to: '/alerts/providers', keywords: 'email sms zeptomail twilio' },
    { label: 'Alerts · Integrations', group: 'Alerting', to: '/alerts/integrations', keywords: 'slack webhook pagerduty' },
    { label: 'Team · Users', group: 'Settings', to: '/team/users' },
    { label: 'Organization settings', group: 'Settings', to: '/settings/organization' },
    { label: 'Preferences', group: 'Settings', to: '/settings/preferences' },
    { label: 'Account · Profile', group: 'Settings', to: '/account/profile' },
    { label: 'Account · Security', group: 'Settings', to: '/account/security', keywords: 'mfa password 2fa' },
    { label: 'Account · API keys', group: 'Settings', to: '/account/api-keys' },
];
export function SearchPalette({ open, onClose }) {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const results = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle)
            return DESTINATIONS;
        return DESTINATIONS.filter((d) => `${d.label} ${d.group} ${d.keywords ?? ''}`.toLowerCase().includes(needle));
    }, [q]);
    useEffect(() => {
        if (open) {
            setQ('');
            setActive(0);
            // focus after paint
            const id = requestAnimationFrame(() => inputRef.current?.focus());
            return () => cancelAnimationFrame(id);
        }
    }, [open]);
    useEffect(() => setActive(0), [q]);
    if (!open)
        return null;
    function go(d) {
        onClose();
        navigate(d.to);
    }
    function onKeyDown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[active])
                go(results[active]);
        }
    }
    return (_jsxs("div", { className: "fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh]", role: "dialog", "aria-modal": "true", "aria-label": "Search", children: [_jsx("div", { onClick: onClose, className: "absolute inset-0 bg-[rgba(8,9,28,0.55)] backdrop-blur-[2px]", "aria-hidden": true }), _jsxs("div", { className: "relative w-full max-w-[560px] overflow-hidden rounded-[var(--r-lg)] border border-[var(--line-strong)] bg-[var(--canvas-2)] shadow-[var(--shadow-xl)]", onKeyDown: onKeyDown, children: [_jsxs("div", { className: "flex items-center gap-2.5 border-b border-[var(--line)] px-4 py-3", children: [_jsx(MagnifyingGlassIcon, { className: "h-[18px] w-[18px] text-[var(--ink-500)]" }), _jsx("input", { ref: inputRef, value: q, onChange: (e) => setQ(e.target.value), placeholder: "Search pages \u2014 domains, alerts, billing\u2026", className: "w-full bg-transparent text-[15px] text-[var(--ink-900)] placeholder:text-[var(--ink-400)] focus:outline-none" }), _jsx("button", { type: "button", "aria-label": "Close search", onClick: onClose, className: "rounded-[6px] border border-[var(--line)] px-1.5 py-0.5 text-[11px] text-[var(--ink-500)] hover:text-[var(--ink-900)] cursor-pointer", style: { fontFamily: 'var(--f-mono)' }, children: "Esc" })] }), _jsx("div", { className: "max-h-[50vh] overflow-y-auto p-1.5", children: results.length === 0 ? (_jsxs("div", { className: "px-3 py-6 text-center text-[13px] text-[var(--ink-500)]", children: ["No matches for \u201C", q, "\u201D."] })) : (results.map((d, i) => (_jsxs("button", { type: "button", onMouseEnter: () => setActive(i), onClick: () => go(d), className: `flex w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left text-[14px] ${i === active ? 'bg-[rgba(124,99,255,0.16)] text-[var(--ink-900)]' : 'text-[var(--ink-700)]'}`, children: [_jsx("span", { children: d.label }), _jsx("span", { className: "text-[11px] uppercase tracking-[0.06em] text-[var(--ink-400)]", style: { fontFamily: 'var(--f-mono)' }, children: d.group })] }, d.to)))) })] })] }));
}
